import { useMemo } from 'react';
import type {
  ArticleHeader,
  ArticleV2 as ArticleV2PageType
} from 'types/capi/article';

import BrandedHeading from '~/components/Content/BrandedHeading';
import BrandedQuote from '~/components/Content/BrandedQuote';
import usePageData from '~/hooks/usePageData';
import useStructuredData from '~/hooks/useStructuredData';
import ArticleCarousel from '~/modules/ArticleCarousel';
import RecentFeatures from '~/modules/RecentFeatures';
import ContentBlock from '~/modules/ste/ContentBlock';
import EmbedBlock from '~/modules/ste/EmbedBlock';
import ImageGrid from '~/modules/ste/ImageGrid';
import ModuleRow from '~/modules/ste/ModuleRow';
import ProductList from '~/modules/ste/ProductList';
import { getModulePositions } from '~/utils/modulePositions';

import CommissionDisclaimer from './CommissionDisclaimer';
import { Footer, Header } from './HeaderFooter';
import HeaderImage from './HeaderImage';

function getHeaderVisibilityClass(header?: ArticleHeader): string | undefined {
  if (!header) return undefined;

  const desktopOverlay = header.desktop.template === 'title_over_image';
  const mobileOverlay = header.mobile.template === 'title_over_image';

  if (desktopOverlay && mobileOverlay) return 'hidden';
  if (desktopOverlay) return 'md:hidden';
  if (mobileOverlay) return 'hidden md:flex';

  return undefined;
}

export default function ArticleV2() {
  const {
    title,
    core_images,
    display_header,
    header,
    author,
    dates,
    article_type,
    _schemas
  } = usePageData<ArticleV2PageType>();

  useStructuredData(_schemas);

  const showHeaderImage = header ? display_header !== false : display_header;
  const headerVisibilityClass = showHeaderImage
    ? getHeaderVisibilityClass(header)
    : undefined;

  return (
    <>
      <CommissionDisclaimer />
      <div className="flex w-full flex-col gap-[64px] bg-grey-tile max-md:gap-6">
        <div>
          {showHeaderImage && (
            <HeaderImage
              header={header}
              images={core_images}
              articleType={article_type}
              title={title}
              author={author}
              publishedDate={dates.published}
              modifiedDate={dates.modified}
            />
          )}
          <Header
            author={author}
            title={title}
            publishedDate={dates.published}
            modifiedDate={dates.modified}
            className={headerVisibilityClass}
          />
        </div>
        <Modules />
        {article_type === 'news' ? <NewsFooterContent /> : <FooterContent />}
      </div>
    </>
  );
}

function Modules() {
  const { modules } = usePageData<ArticleV2PageType>();
  const modulePositions = useMemo(() => getModulePositions(modules), [modules]);

  return (
    <>
      {modules.map((module, index) => {
        switch (module.type) {
          case 'branded-quote':
            return <BrandedQuote key={index} quote={module} />;
          case 'branded-heading':
            return <BrandedHeading key={index} module={module} />;
          case 'content-block':
            return <ContentBlock key={index} {...module} />;
          case 'embed':
            return <EmbedBlock key={index} embed={module} />;
          case 'image-grid':
            return (
              <ImageGrid
                key={index}
                module={module}
                modulePosition={modulePositions.get(module)}
              />
            );
          case 'module-row':
            return (
              <ModuleRow
                key={index}
                module={module}
                modulePositions={modulePositions}
              />
            );
          case 'product-list':
            return (
              <ProductList
                key={index}
                {...module}
                modulePosition={modulePositions.get(module)}
                pageType="article"
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}

function FooterContent() {
  const { author, title, dates, recent_features, latest_news } =
    usePageData<ArticleV2PageType>();

  return (
    <div>
      <div className="w-full bg-black">
        <Footer
          author={author}
          title={title}
          publishedDate={dates.published}
          modifiedDate={dates.modified}
        />
        <div className="mx-auto h-px w-8/12 max-w-[600px] bg-white" />
        {recent_features && (
          <RecentFeatures
            title="Discover more features"
            items={recent_features}
          />
        )}
      </div>
      {latest_news && (
        <ArticleCarousel items={latest_news} title="Related articles" />
      )}
    </div>
  );
}

function NewsFooterContent() {
  const { author, title, dates, recent_features, latest_news } =
    usePageData<ArticleV2PageType>();

  return (
    <div>
      <Footer
        author={author}
        title={title}
        publishedDate={dates.published}
        modifiedDate={dates.modified}
        variant="light"
      />
      {latest_news && (
        <ArticleCarousel items={latest_news} title="Related articles" />
      )}
      <div className="w-full bg-black">
        {recent_features && (
          <RecentFeatures
            title="Discover more features"
            items={recent_features}
          />
        )}
      </div>
    </div>
  );
}
